/* eslint-disable react/display-name */
import React, { memo } from 'react';
import { dataLayer } from '@weatherbug/common';

import { reduxConnect } from '../../../common';
import { useInterval } from '../../../common/hooks/useInterval';
import { DataFetchSpinner } from '../../common/dataFetchSpinner/dataFetchSpinner';
import { IsFetchingObsDataContainer } from './styledComponents/isFetchingObsDataContainer';
import { CurrentConditionsBodyView } from './currentConditionsBodyView';
import type { IStoreState } from '../../common/interfaces/types';

const verbose = false;
const refresh = true;
const defaultObsInterval = 5 * 60 * 1000;

interface CurrentConditionsBodyProps {
    authId: string;
    secret: string;
    stdParameters: string;
    getNowConditions: typeof dataLayer.actions.getNowConditions;
    baseUrl: string;
    forecastBaseUrl: string;
    latitude: number;
    longitude: number;
    temperatureUnit: string;
    nowConditionsIsRefreshing: boolean;
    pageIsVisible: boolean;
    providerId: string;
    stationId: string;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { units, nowConditions, pageVisibility } =  storeState;
    const appState = storeState.appState.appState || {};
    const { authId, secret, forecastBaseUrl, obsBaseUrl, stdParameters, user } = appState;
    const { nowConditionsIsRefreshing } = nowConditions || {};
    const { tempUnit } = units;

    const { Station: stationData, Locality } = user?.ActiveLocation ?? {};
    const { Latitude: stationLatitude, Longitude: stationLongitude, ProviderId, StationId } = stationData || {};
    const pageIsVisible = pageVisibility?.visible;
    const { Latitude, Longitude } = Locality ?? {}; // fallback to active location lat/lon

    return {
        forecastBaseUrl,
        baseUrl: obsBaseUrl,
        latitude: stationLatitude ?? Latitude,
        longitude: stationLongitude ?? Longitude,
        temperatureUnit: tempUnit,
        authId,
        secret,
        stdParameters,
        nowConditionsIsRefreshing,
        pageIsVisible,
        providerId: ProviderId,
        stationId: StationId
    };
};

const mapDispatchToProps = {
    getNowConditions: dataLayer?.actions?.getNowConditions
};

export const CurrentConditionsBody = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    authId,
    secret,
    stdParameters,
    getNowConditions,
    baseUrl,
    forecastBaseUrl,
    latitude,
    longitude,
    temperatureUnit,
    nowConditionsIsRefreshing,
    pageIsVisible,
    providerId,
    stationId
}: CurrentConditionsBodyProps) => {

    useInterval(() => {
        const parameters = {
            latitude,
            longitude,
            providerId,
            stationId,
            temperatureUnit,
            baseUrl,
            authId,
            secret,
            stdParameters,
            verbose,
            refresh
        };
        getNowConditions(parameters);
    }, defaultObsInterval,
    true,
    !!(baseUrl && authId && secret && latitude && longitude && temperatureUnit && pageIsVisible),
    [latitude, longitude, temperatureUnit, pageIsVisible, authId, secret, baseUrl, forecastBaseUrl]);

    return (
        <>
            {nowConditionsIsRefreshing
                && <IsFetchingObsDataContainer>
                    <DataFetchSpinner width={33} height={33} strokeWidth={6}/>
                </IsFetchingObsDataContainer>}
            {!nowConditionsIsRefreshing && <CurrentConditionsBodyView />}
        </>
    );
}));
